/**
 * Rio's interviewer for a job, as the recruiter shapes it.
 *
 * The shapes avrio-backend returns from /api/interviewer and
 * /api/jobs/{jobId}/interviewer. Rio (avrio-rio) owns the meaning: it drafts a
 * config from the posting, applies plain-language edits as a fixed set of
 * operations, validates every change, and builds the interview from the saved
 * config. This app only shows and edits it.
 */

/**
 * The page edits schema 2 settings: every question says whether it counts.
 * Settings saved as schema 1 go back as schema 2, with each of their
 * questions kept as said (counts false): Rio writes a bar for each when
 * they're saved, and none is scored until the recruiter turns counting on.
 */
export const SCHEMA_VERSION = 2;

export const SECTION_KEYS = [
  'logistics',
  'skills',
  'workspace',
  'ai_fluency',
  'candidate_questions',
] as const;
export type SectionKey = (typeof SECTION_KEYS)[number];

export const TONES = ['warm', 'neutral', 'brisk'] as const;
export type Tone = (typeof TONES)[number];

export const WORKSPACE_KINDS = [
  'auto',
  'code',
  'whiteboard',
  'design',
] as const;
export type WorkspaceKind = (typeof WORKSPACE_KINDS)[number];

export interface InterviewerSection {
  key: SectionKey;
  enabled: boolean;
  /** Optional on the wire; the page always fills it in (from Rio's preview). */
  minutes: number;
}

/** What a strong answer shows, as Rio sketches it (schema 2). */
export interface QuestionExpectations {
  excellent: string[];
  adequate: string[];
  weak: string[];
}

export const EXPECTATION_LEVELS = ['excellent', 'adequate', 'weak'] as const;
export type ExpectationLevel = (typeof EXPECTATION_LEVELS)[number];

/** The sections a question of the team's own can be asked in: never the candidate's own questions. */
export const QUESTION_SECTIONS: readonly SectionKey[] = [
  'logistics',
  'skills',
  'workspace',
  'ai_fluency',
];

export interface CustomQuestion {
  id: string;
  text: string;
  section: SectionKey;
  followUp: boolean;
  /** One of Rio's own areas (preview.areas), chosen by Rio; the recruiter can change it. */
  area: string | null;
  /** For the page only: never sent back to Rio, which refuses it inside a config. */
  areaLabel: string | null;
  /** Null until Rio has sketched the bar. */
  expectations: QuestionExpectations | null;
  /**
   * False: asked and shared, "for information only", not scored. A question
   * just added starts out false and takes its bar's answer when the bar
   * arrives (countsFromBar).
   */
  counts: boolean;
  /**
   * For the page only: set on a question just added, until its bar arrives
   * or the recruiter sets counts. Rio is sent no counts for it, so the bar
   * Rio writes on save decides, as here.
   */
  countsFromBar?: boolean;
}

export interface InterviewerConfig {
  schema: number;
  /** All five sections, once each: reordered and switched, never added or removed. */
  sections: InterviewerSection[];
  /**
   * The posting's skill names as Rio stores them (PostingSkill.key), or Rio's
   * own area keys; at most five each, and never the same one twice.
   */
  emphasize: string[];
  deemphasize: string[];
  customQuestions: CustomQuestion[];
  avoidTopics: string[];
  tone: Tone;
  workspace: {
    enabled: boolean;
    /** The first of `kinds`: what an older Rio, which reads only this, opens. */
    kind: WorkspaceKind;
    /**
     * The work tasks, in the order the recruiter chose them: one or more of
     * code, whiteboard and design each once, or `auto` alone. Always set on the
     * page (a config saved before several tasks has [kind]); the card offers
     * more than one only when Rio's preview says `multi`.
     */
    kinds: WorkspaceKind[];
  };
  targetMinutes: number;
  /** Stamped by the server on save. */
  meta: {
    createdFrom: string;
    updatedBy: string | null;
    updatedAt: string | null;
    version: number | null;
  };
}

export interface SkillRef {
  key: string;
  label: string;
}

/** One work task Rio would open, as its preview lists them. */
export interface WorkspaceTask {
  /** The kind it opens as, which for `auto` is what Rio picked. */
  kind: WorkspaceKind;
  label: string;
  minutes: number;
  /** Rio's note on this task, if it has one. */
  note: string | null;
}

export interface PreviewSection {
  key: SectionKey;
  title: string;
  minutes: number;
  enabled: boolean;
  summary: string;
  /** Examples only: each candidate's questions adapt to their own gaps. */
  exampleQuestions: string[];
}

export interface InterviewerPreview {
  totalMinutes: number;
  sections: PreviewSection[];
  workspace: {
    enabled: boolean;
    kind: WorkspaceKind;
    label: string;
    /** Set when the chosen kind isn't available yet, e.g. "coming soon". */
    note: string | null;
    /**
     * The kinds that open as asked where this Rio runs (whiteboard and design
     * only once Rio's canvases switch is on), in WORKSPACE_KINDS order. null
     * when Rio didn't say: an older Rio, or no preview yet.
     */
    availableKinds: WorkspaceKind[] | null;
    /**
     * For every kind, Rio's notes on the work task if the recruiter picked it
     * with the task on, the last being `note`. Lets the card show the right
     * notes as soon as the kind changes. null when Rio didn't say (an older
     * Rio).
     */
    notesByKind: Record<WorkspaceKind, string[]> | null;
    /**
     * Whether this Rio opens several work tasks (its WORKSPACE_MULTI switch).
     * False, and an older Rio's absence of the field, mean one task: the kind.
     */
    multi: boolean;
    /** With `multi`: the set Rio read, in order. Otherwise null. */
    kinds: WorkspaceKind[] | null;
    /**
     * With `multi`: what Rio would open for this role, in order (a kind it has
     * no task for is left out). Otherwise null.
     */
    tasks: WorkspaceTask[] | null;
    /**
     * With `multi`: the tasks' minutes added up (not the interview's own
     * `totalMinutes`). Otherwise null.
     */
    tasksMinutes: number | null;
  };
  emphasis: { emphasize: SkillRef[]; deemphasize: SkillRef[] };
  customQuestions: PreviewQuestion[];
  notes: string[];
  /** Rio's own areas a question can score into: the area picker's options. */
  areas: SkillRef[];
}

export interface PreviewQuestion {
  id: string;
  text: string;
  section: SectionKey;
  area: string | null;
  areaLabel: string | null;
  expectations: QuestionExpectations | null;
  counts: boolean;
}

export interface PostingSkill {
  /** The name as Rio stores it in emphasis: the label, spaces collapsed, at most 60 characters. */
  key: string;
  label: string;
  required: boolean;
}

/** What Rio drafts from: the posting as the recruiter has written it so far. */
export interface Posting {
  title: string;
  description: string;
  skills: PostingSkill[];
}

export interface AppliedChange {
  op: string;
  summary: string;
}

export interface RejectedChange extends AppliedChange {
  reason: string;
}

export interface DiffEntry {
  path: string;
  before: unknown;
  after: unknown;
}

export interface DraftResult {
  config: InterviewerConfig;
  preview: InterviewerPreview;
}

export interface EditResult extends DraftResult {
  applied: AppliedChange[];
  rejected: RejectedChange[];
  diff: DiffEntry[];
}

export interface SavedInterviewer {
  /** Null means the job uses Rio's default interviewer. */
  config: InterviewerConfig | null;
  /** The version to save against next; null for a job never saved. */
  version: number | null;
  /** With no config, Rio's default draft for the posting. */
  preview: InterviewerPreview;
}

export interface SaveResult {
  config: InterviewerConfig;
  version: number | null;
  preview: InterviewerPreview;
}

/** Rio's bar for one question as it's added (draft_bar). */
export interface DraftedBar {
  /** Null only when Rio couldn't write a bar at all. */
  area: string | null;
  areaLabel: string | null;
  expectations: QuestionExpectations | null;
  /** True when the bar is complete, so the question can count. */
  counts: boolean;
  /** Rio's word on anything short of a complete bar, for the recruiter. */
  note: string | null;
}

export interface ValidationResult {
  valid: boolean;
  problems: FieldError[];
  preview: InterviewerPreview | null;
}

export interface HistoryVersion {
  version: number;
  updatedAt: string;
  /** A colleague's name, or "A teammate": never a staff id. */
  updatedBy: string;
  /** Worked out on the page from the change against the version before. */
  summary: string;
}

export interface FieldError {
  path: string;
  message: string;
}

export interface InterviewerService {
  /** Not saved. Works before the job exists. */
  draft(posting: Posting): Promise<DraftResult>;
  /** Not saved: the recruiter keeps or undoes the result. */
  edit(
    config: InterviewerConfig,
    instruction: string,
    posting: Posting,
  ): Promise<EditResult>;
  /** Not saved. Rio's bar for one question of the team's own, as it's added. */
  draftBar(
    question: { text: string; section: SectionKey },
    posting: Posting,
  ): Promise<DraftedBar>;
  get(jobId: number): Promise<SavedInterviewer>;
  /**
   * Rejects with a `conflict` error when someone else saved first. The
   * version is 0 for a job with no settings yet.
   */
  save(
    jobId: number,
    config: InterviewerConfig,
    expectedVersion: number | null,
  ): Promise<SaveResult>;
  /**
   * Back to Rio's default interviewer. History is kept. With the version the
   * recruiter was looking at, rejects with a `conflict` error when someone
   * else saved since.
   */
  reset(
    jobId: number,
    expectedVersion: number | null,
  ): Promise<{ version: number | null }>;
  history(jobId: number): Promise<HistoryVersion[]>;
  /**
   * A quick check, e.g. of a custom question as it's typed. Always with the
   * posting: Rio checks emphasis against its skills.
   */
  validate(
    config: InterviewerConfig,
    posting: Posting,
  ): Promise<ValidationResult>;
}
